import React, { useState, useEffect } from "react";
// ANT DESIGN COMPONENTS
import { Spin, Button, Switch } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { faGears } from "@fortawesome/free-solid-svg-icons";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { formatUuidT } from "../../flatbuffers/display";
import {
  updateGlobalOrganizationName,
  updateGlobalOrganizationId,
  updateGlobalRolId,
  updateGlobalRolName,
} from "../../redux/home/homeSlice";
import { updatePermissions } from "../../redux/users/userSlice";
import { useLazyGetRoleQuery } from "../../redux/roles/roleAPI";
import { updateShowHeaderConfiguration } from "../../redux/monitoring/monitoringSlice";
// COMPONENTS
import DrawerRowDivider from "../components/drawer/DrawerRowDivider";
import SelectOrganizationsLight from "../components/select/SelectOrganizationsLight";
import SelectRolesLight from "../components/select/SelectRolesLight";
import GlobalAlert2 from "./GlobalAlert2";
// INTERFACES
import { RootState } from "../../app/store";
// AUTHORIZATION
import GETJwtToken from "../../redux/authentication/authentication";
// UTILS
import { downloadJSON } from "../../utils/utils";

function GlobalConfiguration() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const { theme } = useSelector((state: RootState) => state.home);
  const [t] = useTranslation("global");
  const dispatch = useDispatch();

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [jsonObjectPermissions, setJsonObjectPermissions] = useState<any>({});

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { globalOrganizationId, globalRolId, userLogged, globalRolName } = useSelector(
    (state: RootState) => state.home,
  );

  const { showHeaderConfiguration } = useSelector((state: RootState) => state.monitoring);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [
    triggerGetAdaptersTable,
    {
      isLoading: isLoadingGetRole,
      isFetching: isFetchingGetRole,
      isError: isErrorGetRole,
      error: errorGetRole,
    },
  ] = useLazyGetRoleQuery();

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const onChangeIdOrganization = (idOrganization: string, record: any) => {
    dispatch(updateGlobalOrganizationId(record.value));
    dispatch(updateGlobalOrganizationName(record.label));
    // SelectRolesLight scopes its options to globalOrganizationId
    // (useGetAllRolesLight({ orgId })), so a role previewed for the
    // PREVIOUS org is never in the new org's role list. Reset back to the
    // master admin's own role whenever the org changes, so `permissions`
    // (and every validatePermission() gate app-wide) can't keep applying a
    // role that doesn't belong to the org now being viewed. This panel is
    // only rendered for MASTER ADMIN (see YourProfileDrawer), so the reset
    // is unconditional here.
    onChangeIdRole(userLogged.roleId);
  };

  const onChangeIdRole = async (newValue: string) => {
    try {
      const token = await GETJwtToken();
      const result = await triggerGetAdaptersTable({ token, roleId: newValue });
      if (result && result.data && result.data.data) {
        setJsonObjectPermissions(result.data.data.menu);
        dispatch(updatePermissions(result.data.data.menu));
        dispatch(
          updateGlobalRolId(
            typeof result.data.data.id === "string"
              ? result.data.data.id
              : formatUuidT(result.data.data.id),
          ),
        );
        dispatch(updateGlobalRolName(String(result.data.data.name ?? "")));
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const onClickResetToMasterAdmin = () => {
    dispatch(updateGlobalOrganizationId(userLogged.organizationId));
    dispatch(updateGlobalOrganizationName(String(userLogged.org.name ?? "")));
    onChangeIdRole(userLogged.roleId);
  };

  const onClickDownloadJson = () => {
    downloadJSON(jsonObjectPermissions, globalRolName);
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if (userLogged && userLogged.role) {
      setJsonObjectPermissions(userLogged.role.menu);
    }
  }, [userLogged]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      <br />
      {/** ************************************************* */}
      {/** COMPONENT DRAWER DIVIDER */}
      <DrawerRowDivider icon={faGears} title={t("general.globalConfiguration")} />
      <div className={`drawer__box__${theme}`}>
        <Spin spinning={isLoadingGetRole || isFetchingGetRole}>
          {/** ENABLE HEADER CONFIGURATION */}
          <div className="col-md-12 mt-1">
            <div className="generalStyles__flex">
              <div className="generalStyles__inputFlexNameLarge">
                {t("general.enableHeaderConfiguration")}
              </div>
              <div className="generalStyles__width100">
                <Switch
                  checked={showHeaderConfiguration}
                  onChange={() => {
                    dispatch(updateShowHeaderConfiguration(!showHeaderConfiguration));
                  }}
                />
              </div>
              <div style={{ width: "52px" }} />
            </div>
          </div>
          {/** ORGANIZATION */}
          <div className="col-md-12 mt-3">
            <div className="generalStyles__flex">
              <div className="generalStyles__inputFlexNameLarge">{t("general.organization")}</div>
              <div className="generalStyles__width100">
                <SelectOrganizationsLight
                  value={globalOrganizationId}
                  onChangeIdOrganization={onChangeIdOrganization}
                  disabledInput={false}
                />
              </div>
              <div style={{ width: "52px" }} />
            </div>
          </div>
          {/** ROLE */}
          <div className="col-md-12 mt-3">
            <div className="generalStyles__flex">
              <div className="generalStyles__inputFlexNameLarge">{t("general.role")}</div>
              <div className="generalStyles__width100">
                <SelectRolesLight
                  value={globalRolId}
                  onChangeIdRole={onChangeIdRole}
                  disabledInput={false}
                />
              </div>
              <div style={{ width: "52px" }} />
            </div>
          </div>
          {/** COPY JSON OBJECT */}
          <div className="col-md-12 mt-3">
            <div className="generalStyles__flex">
              <div className="generalStyles__inputFlexNameLarge">JSON Object</div>
              <div className="generalStyles__width100">
                <Button
                  size="small"
                  className="buttonStyle__1"
                  style={{ width: "100%" }}
                  onClick={onClickDownloadJson}
                >
                  Download JSON
                </Button>
              </div>
              <div style={{ width: "52px" }} />
            </div>
          </div>
          {/** RESET */}
          <div className="col-md-12 mt-3">
            <div className="generalStyles__flex">
              <div className="generalStyles__inputFlexNameLarge">
                {t("general.resetToMasterAdmin")}
              </div>
              <div className="generalStyles__width100">
                <Button
                  size="small"
                  className="buttonStyle__1"
                  style={{ width: "100%" }}
                  onClick={onClickResetToMasterAdmin}
                >
                  {t("general.resetToMasterAdmin")}
                </Button>
              </div>
              <div style={{ width: "52px" }} />
            </div>
          </div>
        </Spin>
      </div>
      <GlobalAlert2
        isError={isErrorGetRole}
        isSuccess={false}
        requestType="GET"
        error={errorGetRole}
        name="Role"
      />
    </div>
  );
}

export default GlobalConfiguration;
